import React, { useState, useEffect } from 'react';
import { AlertTriangle, Phone, MapPin, Loader2, Shield, CheckCircle, X, User } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

/**
 * DEDICATED EMERGENCY SOS PAGE
 * - Loads instantly with minimal UI
 * - No login required
 * - Can be added to phone homescreen as shortcut
 * - Alerts dispatch AND calls 911 simultaneously
 */
const SOSPage = () => {
  const [stage, setStage] = useState('ready'); // ready, confirming, sending, sent, cancelled
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [countdown, setCountdown] = useState(10);
  const [alertId, setAlertId] = useState(null);
  
  // Optional contact info
  const [showContactForm, setShowContactForm] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  // Get location immediately on page load
  useEffect(() => {
    getLocation();
  }, []);

  // Countdown timer with vibration feedback
  useEffect(() => {
    let timer;
    if (stage === 'confirming' && countdown > 0) {
      // Vibrate on each countdown tick (if supported)
      if (navigator.vibrate) {
        navigator.vibrate([200, 100, 200]);
      }
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (stage === 'confirming' && countdown === 0) {
      // Final long vibration before sending
      if (navigator.vibrate) {
        navigator.vibrate([500]);
      }
      sendEmergencyAlert();
    }
    return () => clearTimeout(timer);
  }, [stage, countdown]);

  const getLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Location not available');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
        
        // Get address
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await response.json();
          if (data.display_name) {
            setLocation(prev => ({ ...prev, address: data.display_name }));
          }
        } catch (e) {
          console.log('Could not get address');
        }
      },
      (error) => {
        setLocationError('Enable location for better response');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const startEmergency = () => {
    setStage('confirming');
    setCountdown(10);
    // Initial vibration burst to confirm action
    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200, 100, 200]);
    }
  };

  const cancelEmergency = () => {
    setStage('ready');
    setCountdown(10);
    // Stop any vibration
    if (navigator.vibrate) {
      navigator.vibrate(0);
    }
  };

  const sendEmergencyAlert = async () => {
    setStage('sending');
    
    try {
      // Send alert to dispatch
      const alertData = {
        latitude: location?.latitude || 0,
        longitude: location?.longitude || 0,
        location_address: location?.address || 'Location unavailable',
        alert_type: 'sos',
        message: 'EMERGENCY SOS - Driver needs immediate help',
        guest_name: name || 'Emergency User',
        guest_phone: phone || 'Not provided',
      };

      const response = await api.post('/sos/alert/guest', alertData);
      
      if (response.data.success) {
        setAlertId(response.data.alert_id);
        setStage('sent');
        
        // SIMULTANEOUSLY call 911 after a brief delay to ensure alert is sent
        setTimeout(() => {
          window.location.href = 'tel:911';
        }, 500);
      }
    } catch (error) {
      console.error('Alert failed:', error);
      // Even if API fails, still try to call 911
      setStage('sent');
      setTimeout(() => {
        window.location.href = 'tel:911';
      }, 500);
    }
  };

  const call911Only = () => {
    window.location.href = 'tel:911';
  };

  const cancelAlert = async () => {
    if (alertId) {
      try {
        await api.delete(`/sos/alerts/${alertId}/cancel/guest`);
      } catch (e) {
        console.log('Could not cancel');
      }
    }
    setStage('cancelled');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-900 via-red-800 to-red-900 flex flex-col items-center justify-center p-4">
      {/* Header */}
      <div className="text-center mb-6">
        <Shield className="w-16 h-16 text-white mx-auto mb-2" />
        <h1 className="text-3xl font-bold text-white">HaulLogix SOS</h1>
        <p className="text-red-200 text-sm">Emergency Alert System</p>
      </div>

      {/* Location Status */}
      <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-2 mb-6 flex items-center gap-2">
        <MapPin className={`w-4 h-4 ${location ? 'text-green-400' : 'text-yellow-400 animate-pulse'}`} />
        <span className="text-white text-sm">
          {location ? 'Location ready' : locationError || 'Getting location...'}
        </span>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-sm">
        {stage === 'ready' && (
          <div className="space-y-4">
            {/* Main SOS Button */}
            <button
              onClick={startEmergency}
              className="w-full aspect-square max-w-[280px] mx-auto rounded-full bg-red-600 hover:bg-red-500 border-8 border-red-400 shadow-2xl flex flex-col items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
              style={{ boxShadow: '0 0 60px rgba(239, 68, 68, 0.6)' }}
            >
              <AlertTriangle className="w-20 h-20 text-white mb-2" />
              <span className="text-white text-2xl font-bold">SOS</span>
              <span className="text-red-200 text-sm">TAP FOR HELP</span>
            </button>

            {/* Call 911 Only */}
            <button
              onClick={call911Only}
              className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 shadow-lg"
            >
              <Phone className="w-6 h-6" />
              CALL 911 ONLY
            </button>

            {/* Optional: Add contact info */}
            {!showContactForm ? (
              <button
                onClick={() => setShowContactForm(true)}
                className="w-full py-2 text-white/70 text-sm underline"
              >
                Add your name/phone (optional)
              </button>
            ) : (
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 text-white text-sm mb-2">
                  <User className="w-4 h-4" />
                  <span>Contact Info (helps dispatch reach you)</span>
                </div>
                <input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white/20 text-white placeholder-white/50 border border-white/30"
                />
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white/20 text-white placeholder-white/50 border border-white/30"
                />
                <button
                  onClick={() => setShowContactForm(false)}
                  className="text-white/50 text-sm"
                >
                  Done
                </button>
              </div>
            )}

            <p className="text-center text-white/60 text-xs mt-4">
              SOS alerts dispatch AND calls 911 simultaneously.
              <br />
              No login required.
            </p>
          </div>
        )}

        {stage === 'confirming' && (
          <div className="text-center">
            <div className="relative w-40 h-40 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-8 border-white/30"></div>
              <div 
                className="absolute inset-0 rounded-full border-8 border-white border-t-transparent animate-spin"
                style={{ animationDuration: '1s' }}
              ></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-7xl font-bold text-white">{countdown}</span>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Sending in {countdown}...
            </h2>
            <p className="text-red-200 mb-6">
              Will alert dispatch & call 911
            </p>
            <button
              onClick={cancelEmergency}
              className="px-8 py-3 bg-white/20 hover:bg-white/30 text-white rounded-xl font-semibold flex items-center justify-center gap-2 mx-auto"
            >
              <X className="w-5 h-5" />
              CANCEL
            </button>
          </div>
        )}

        {stage === 'sending' && (
          <div className="text-center">
            <Loader2 className="w-20 h-20 text-white mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-white">
              Sending Alert...
            </h2>
            <p className="text-red-200">Contacting dispatch & 911</p>
          </div>
        )}

        {stage === 'sent' && (
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-4 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle className="w-14 h-14 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              HELP IS COMING!
            </h2>
            <p className="text-green-200 mb-6">
              Dispatch notified. 911 dialing...
              <br />
              Stay calm. Stay where you are if safe.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={call911Only}
                className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Call 911 Again
              </button>
              
              <button
                onClick={cancelAlert}
                className="w-full py-3 bg-white/20 hover:bg-white/30 text-white rounded-xl flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel (False Alarm)
              </button>
            </div>
          </div>
        )}

        {stage === 'cancelled' && (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-gray-500 rounded-full flex items-center justify-center">
              <X className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white mb-4">
              Alert Cancelled
            </h2>
            <button
              onClick={() => setStage('ready')}
              className="px-6 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-semibold"
            >
              Back to SOS
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-white/40 text-xs">
          Add this page to your homescreen for quick access
        </p>
        <a href="/" className="text-white/60 text-sm underline mt-2 inline-block">
          Return to HaulLogix
        </a>
      </div>
    </div>
  );
};

export default SOSPage;
